import React from 'react';
import { createPortal } from 'react-dom';
import { CSSTransitionGroup } from '../../../node_module_clones/react-transition-group';
import classNames from 'classnames';

// Context
import { ToastData, useToastRendererContext } from './ToastContext';

// Utils
import { getToastContainer } from './utils/toastUtils';

// Styles
import './ToastRenderer.scss';

const ToastRenderer: React.FC<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> = (
    props,
) => {
    const toasts = useToastRendererContext();

    // Group toasts by their parent class
    const toastGroups = React.useMemo(() => {
        const groups = new Map<string, Array<[string, ToastData]>>();

        [...toasts].forEach(([toastId, toastData]) => {
            const key = toastData.groupId || 'default';
            const group = groups.get(key) || [];
            group.push([toastId, toastData]);
            groups.set(key, group);
        });

        return groups;
    }, [toasts]);

    return (
        <>
            {[...toastGroups].map(([parentKey, groupToasts]) => {
                const isGrouped = parentKey !== 'default';
                const container = isGrouped ? getToastContainer(parentKey) : document.body;

                const content = (
                    <div
                        {...props}
                        className={classNames(
                            'ToastRenderer',
                            {
                                grouped: isGrouped,
                            },
                            props.className,
                        )}
                        key={parentKey}
                    >
                        <CSSTransitionGroup
                            transitionName="fade-and-slide"
                            transitionAppear
                            transitionEnterTimeout={300}
                            transitionLeaveTimeout={300}
                            transitionAppearTimeout={300}
                        >
                            {groupToasts.map(([toastId, toastData], index) => (
                                <div
                                    key={toastId}
                                    {...toastData.props}
                                    className={classNames('Toast', toastData.className, {
                                        grouped: isGrouped,
                                    })}
                                    // Add the index as a custom property so we can perform CSS positioning and transitions using it
                                    style={{ '--index': index } as React.CSSProperties}
                                >
                                    {toastData.children}
                                </div>
                            ))}
                        </CSSTransitionGroup>
                    </div>
                );

                return isGrouped && container !== document.body
                    ? createPortal(content, container, `toast-group-${parentKey}`)
                    : content;
            })}
        </>
    );
};

export default React.memo(ToastRenderer);
